import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST, CHARACTER_LENGTH } from "@constants/index";
import DeleteButton from "@components/Buttons/DeleteButton";
import OutlineButton from "@components/Buttons/OutlineButton";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CustomInput, CustomDatePicker } from "@components/Forms";
import { ChevronUpIcon } from "@components/Icons";
import PreviewSection from "@components/Preview/PreviewSection";
import DocumentTable from "@components/Tables/DocumentTable";
import TableWithPagination from "@components/Tables/TableWithPagination";
import TabsContainer from "@components/Tabs/TabsContainer";
import { Breadcrumb, ConfirmProcessingModal, VerificationRequestButton } from "@components/UI";
import CustomSelect from "@components/UI/CustomSelect";
import LicenseSelectionModal from "@components/UI/LicenseSelectionModal";
import MemberSelectionModal from "@components/UI/MemberSelectionModal";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import queryKeys from "@configs/react-query/query-key";
import { useFetchData } from "@hooks/useFetchData";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { useUploadFileWithGit } from "@hooks/useUploadFileWithGit";
import "@styles/project-detail.css";
import { formatDate, normalizeResponseData, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";

import { useTranslation } from "react-i18next";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import { useClarityVerificationRequest } from "@hooks/useClarityVerificationRequest";
import { useFossIdVerificationRequest } from "@hooks/useFossIdVerificationRequest";
import { useSbomVerificationRequest } from "@hooks/useSbomVerificationRequest";
import { formatDateTime, formatFileSize } from "@utils/common";
import { PrimaryButton } from "@components/Buttons";
import { useClientConfig } from "@contexts/ClientConfigContext";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import ScanningBlockModal from "@components/UI/ScanningBlockModal";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { useUploadBlocking } from "@hooks/useUploadBlocking";
import { useClarityAirVerificationRequest } from "@hooks/useClarityAirVerificationRequest";
import { useProjectDetailPermissions } from "@hooks/useProjectDetailPermissions";

const ProjectDetail = () => {
  const { colors } = useTheme();
  const { projectSeq } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { cloneRepositoryViaPta, cloneRepositoryViaSsh } = useUploadFileWithGit();
  const [isCloneRepositoryPending, setIsCloneRepositoryPending] = useState(false);

  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [pendingReuseCallback, setPendingReuseCallback] = useState(null);
  const [pendingNewScanCallback, setPendingNewScanCallback] = useState(null);
  const [showConfirmProcessingModal, setShowConfirmProcessingModal] = useState(false);
  const isFirstRender = useRef(true);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [distributionScopeList, setDistributionScopeList] = useState([]);
  const [modalSelectLicenseOpen, setModalSelectLicenseOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const { assetClassificationsList, creatorList, departmentList, scanToolList, projectManagerList, fetchSearchInfo } = useSearchInfo();
  const [myProductsList, setMyProductsList] = useState([]);
  const [creatorOptions, setCreatorOptions] = useState([]);

  const getOriginalProductSeq = (projectData) => {
    if (projectData?.product?.productSeq != null) return parseInt(projectData.product.productSeq);
    return null;
  };

  const renderProductNameDisplay = () => {
    if (getData.isLoading || !displayProjectData) return "";
    return displayProjectData.product?.productName || "---";
  };
  const [fileGroupNo, setFileGroupNo] = useState(null);
  const { fileInfo, uploadFiles } = useFileGroupAPI(fileGroupNo || 0);

  const [projectData, setProjectData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [documentTotalData, setDocumentTotalData] = useState(0);
  const [processingRowId, setProcessingRowId] = useState(null);
  const [licensePolicyList, setLicensePolicyList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const TEST_ID = "project-detail";
  const fieldTestId = (name) => `${TEST_ID}-field-${name}`;
  const valueTestId = (name) => `${TEST_ID}-value-${name}`;

  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [documentItemsPerPage] = useState(5);
  const documentTotalPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);
  const [verificationType, setVerificationType] = useState("");

  // Set active tab based on location state when scanToolList is available
  const [defaultTabIndex, setDefaultTabIndex] = useState(0);
  // Add new state for controlling active verification tab
  const [activeVerificationTab, setActiveVerificationTab] = useState(0);
  // Get the query parameters from the URL
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get("activeTab") || "defaultTab";

  //Set the default and active tab
  useEffect(() => {
    if (activeTab && scanToolList && Array.isArray(scanToolList)) {
      const tabIndex = scanToolList.findIndex(tool =>
        tool?.value?.toLowerCase() === activeTab.toLowerCase()
      );
      if (tabIndex >= 0) {
        setDefaultTabIndex(tabIndex);
        setActiveVerificationTab(tabIndex);
      }
    }
  }, [activeTab, scanToolList]);

  useEffect(() => {
    try {
      if (location?.state?.activeTab && scanToolList && Array.isArray(scanToolList) && scanToolList.length > 0) {
        const tabIndex = scanToolList.findIndex(tool =>
          tool?.value?.toLowerCase() === location.state.activeTab.toLowerCase()
        );
        if (tabIndex >= 0) {
          setDefaultTabIndex(tabIndex);
          // Reset location state after using it
          navigate(location.pathname, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error setting default tab:', error);
      setDefaultTabIndex(0);
    }
  }, [location?.state?.activeTab, scanToolList, navigate]);

  // Separate effect for localStorage to avoid interfering with existing logic
  useEffect(() => {
    try {
      // Only run if we don't have location state (to avoid conflicts)
      if (!location?.state?.activeTab) {
        // Safely check localStorage with try-catch
        let preferredTab = null;
        let lastProjectSeq = null;

        try {
          preferredTab = localStorage.getItem('preferredTab');
          lastProjectSeq = localStorage.getItem('lastProjectSeq');
        } catch (e) {
          console.error('Error accessing localStorage:', e);
          return; // Exit early if localStorage access fails
        }

        // Check localStorage for preferred tab (set when navigating away from verification)
        if (preferredTab &&
          lastProjectSeq === projectSeq &&
          scanToolList && Array.isArray(scanToolList) && scanToolList.length > 0) {

          const tabIndex = scanToolList.findIndex(tool =>
            tool?.value?.toLowerCase() === preferredTab.toLowerCase()
          );

          if (tabIndex >= 0) {
            setDefaultTabIndex(tabIndex);
            setActiveVerificationTab(tabIndex);

            // Safely clear the preference after using it
            try {
              localStorage.removeItem('preferredTab');
              localStorage.removeItem('lastProjectSeq');
            } catch (e) {
              console.error('Error removing items from localStorage:', e);
            }
          }
        }
      }
    } catch (error) {
      // Catch any unexpected errors to prevent app crash
      console.error('Error processing localStorage tab preference:', error);
    }
  }, [scanToolList, projectSeq, location?.state?.activeTab]);

  // Create a custom mutation for getting file information with dynamic URL and pagination (fileGroupNo as query param)
  const getFileInfoMutation = useMutationData(
    ({ fileGroupNo, page = 1, pageSize = 5 }) => {
      const startRow = (page - 1) * pageSize;
      return `${queryKeys.getFileInfo}?fileGroupNo=${fileGroupNo}&pageSize=${pageSize}&startRow=${startRow}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response.object)) {
          setFileInfoModified(response.object);
          setDocumentTotalData(response.objectCount || response.object.length);
        } else if (Array.isArray(response)) {
          setFileInfoModified(response);
          setDocumentTotalData(response.length);
        } else {
          setFileInfoModified([]);
          setDocumentTotalData(0);
        }
      },
      onError: (error) => {
        console.error("Error during get file info:", error);
        setFileInfoModified([]);
        setDocumentTotalData(0);
      },
    },
  );
  const [allMembersInProject, setAllMembersInProject] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const [memberSelectedRows, setMemberSelectedRows] = useState([]);
  const [isProjectInfoCollapsed, setIsProjectInfoCollapsed] = useState(false);
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberItemsPerPage] = useState(5);
  const [memberTotalPages, setMemberTotalPages] = useState(1);
  const [memberTotalData, setMemberTotalData] = useState(0);
  const [sbomCurrentPage, setSbomCurrentPage] = useState(1);
  const clientConfigContext = useClientConfig();

  // Polling interval for verification status updates
  const pollingIntervalRef = useRef(null);
  // Latest upload/duplicate flags for polling (setInterval closure stays stale otherwise)
  const clarityUploadPendingRef = useRef(false);
  const clarityDuplicateCheckPendingRef = useRef(false);
  const fossidUploadPendingRef = useRef(false);
  const fossidDuplicateCheckPendingRef = useRef(false);
  const clarityAirUploadPendingRef = useRef(false);
  const clarityAirDuplicateCheckPendingRef = useRef(false);

  // Safe destructuring with null checks
  const clientConfig = clientConfigContext?.clientConfig || {};
  const clientConfigLoading = clientConfigContext?.isLoading || false;
  const getClientConfigValue = clientConfigContext?.getClientConfigValue || (() => null);

  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);

  // Use the three verification hooks directly
  const {
    verificationData: clarityData,
    getVerificationData: getClarityData,
    handleSubmitVerificationRequest: handleClaritySubmit,
    verificationUpload: clarityUpload,
    isDuplicateCheckPending: isClarityDuplicateCheckPending,
  } = useClarityVerificationRequest();

  const {
    verificationData: fossidData,
    getVerificationData: getFossidData,
    handleSubmitVerificationRequest: handleFossidSubmit,
    verificationUpload: fossidUpload,
    isDuplicateCheckPending: isFossidDuplicateCheckPending,
    isCreateVerificationPending: isFossidCreatePending,
  } = useFossIdVerificationRequest();

  const {
    verificationData: sbomData,
    listData: sbomListData,
    getVerificationData: getSbomData,
    handleSubmitVerificationRequest: handleSbomSubmit,
    createSbomVerification: sbomCreate,
    isDuplicateCheckPending: isSbomDuplicateCheckPending,
  } = useSbomVerificationRequest(sbomCurrentPage);

  const {
    verificationData: clarityAirData,
    getVerificationData: getClarityAirData,
    handleSubmitVerificationRequest: handleClarityAirSubmit,
    verificationUpload: clarityAirUpload,
    isDuplicateCheckPending: isClarityAirDuplicateCheckPending,
    isCreateVerificationPending: isClarityAirCreatePending,
  } = useClarityAirVerificationRequest();

  // Upload Blocking State
  const isUploading =
    isClarityDuplicateCheckPending ||
    isFossidDuplicateCheckPending ||
    isSbomDuplicateCheckPending ||
    isFossidCreatePending ||
    sbomCreate.isPending ||
    clarityUpload.isPending ||
    fossidUpload.isPending ||
    isCloneRepositoryPending ||
    isClarityAirDuplicateCheckPending ||
    isClarityAirCreatePending ||
    clarityAirUpload.isPending;

  clarityUploadPendingRef.current = clarityUpload.isPending;
  clarityDuplicateCheckPendingRef.current = isClarityDuplicateCheckPending;
  fossidUploadPendingRef.current = fossidUpload.isPending;
  fossidDuplicateCheckPendingRef.current = isFossidDuplicateCheckPending;
  clarityAirUploadPendingRef.current = clarityAirUpload.isPending;
  clarityAirDuplicateCheckPendingRef.current = isClarityAirDuplicateCheckPending;

  // Use upload blocking hook
  const {
    showNavModal,
    showRefreshModal,

    handleCloseNavModal,
    handleCloseRefreshModal,
  } = useUploadBlocking(isUploading);

  // Fetch data on component mount
  useEffect(() => {
    if (projectSeq) {
      // Fetch clarity verification data
      getClarityData.mutate({ projectSeq: projectSeq });

      // Fetch fossid verification data
      getFossidData.mutate(projectSeq);

      // Fetch sbom verification data
      getSbomData.mutate(projectSeq);

      // Fetch sbom verification data
      getClarityAirData.mutate(projectSeq);

      if (location.state?.refreshData) {
        // Also refresh verification data when refreshData flag is set
        getClarityData.mutate({ projectSeq: projectSeq });
        getFossidData.mutate(projectSeq);
        getSbomData.mutate(projectSeq);
        getClarityAirData.mutate({ projectSeq: projectSeq });
        navigate(location.pathname, { state: { ...location.state, refreshData: false }, replace: true });
      }
    }
  }, [projectSeq, location?.state?.refreshData]);

  useEffect(() => {
    // Safe check for preferences context and data
    if (clientConfigContext && !clientConfigLoading && clientConfig && Object.keys(clientConfig).length > 0) {
      try {
        const usePreview = getClientConfigValue("usePreview");
        if (usePreview !== null && usePreview !== undefined) {
          setIsPreviewEnabled(usePreview === "Y");
        }
      } catch (error) {
        console.error('Error getting preference value:', error);
        // Fallback to default value
        setIsPreviewEnabled(true);
      }
    }
  }, [clientConfigContext, clientConfig, clientConfigLoading, getClientConfigValue]);

  // Polling effect to check verification status updates
  useEffect(() => {
    const hasProcessingVerifications = () => {
      const processingStatuses = ['ON_ANALYSIS'];

      const clarityProcessingRaw = clarityData?.some(item =>
        processingStatuses.includes(item.verificationStatusCd) &&
        (item.clarityScanStatusCdName !== 'Success' && item.clarityScanStatusCdName !== 'Fail')
      );

      const fossidProcessingRaw = fossidData?.some(item =>
        processingStatuses.includes(item.verificationStatusCd) &&
        (item.fossidScanStatusCdName !== 'Success' && item.fossidScanStatusCdName !== 'Fail')
      );

      const sbomProcessing = sbomData?.some(item =>
        processingStatuses.includes(item.verificationStatusCd) &&
        (item.sbomScanStatusCdName !== 'Success' && item.sbomScanStatusCdName !== 'Fail')
      );

      const clarityAirProcessingRaw = clarityAirData?.some(item =>
        processingStatuses.includes(item.verificationStatusCd) &&
        (item.clarityAirScanStatusCdName !== 'Success' && item.clarityAirScanStatusCdName !== 'Fail')
      );

      const clarityProcessing =
        Boolean(clarityProcessingRaw) &&
        !clarityUploadPendingRef.current &&
        !clarityDuplicateCheckPendingRef.current;

      const fossidProcessing =
        Boolean(fossidProcessingRaw) &&
        !fossidUploadPendingRef.current &&
        !fossidDuplicateCheckPendingRef.current;

      const clarityAirProcessing =
        Boolean(clarityAirProcessingRaw) &&
        !clarityAirUploadPendingRef.current &&
        !clarityAirDuplicateCheckPendingRef.current;

      return {
        clarityProcessingRaw,
        fossidProcessingRaw,
        sbomProcessing,
        clarityProcessing,
        fossidProcessing,
        clarityAirProcessingRaw,
        clarityAirProcessing,
      };
    };

    const {
      clarityProcessingRaw,
      fossidProcessingRaw,
      sbomProcessing,
      clarityAirProcessingRaw,
    } = hasProcessingVerifications();

    // Start polling if any verification is still processing on the server (raw flags keep interval alive during uploads)
    if (projectSeq && (clarityProcessingRaw || fossidProcessingRaw || sbomProcessing || clarityAirProcessingRaw)) {
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(() => {
          const currentStatus = hasProcessingVerifications();
          if (currentStatus.clarityProcessing) {
            getClarityData.mutate({ projectSeq });
          }
          if (currentStatus.fossidProcessing) {
            getFossidData.mutate(projectSeq);
          }
          if (currentStatus.clarityAirProcessing) {
            getClarityAirData.mutate(projectSeq);
          }
          if (currentStatus.sbomProcessing) getSbomData.mutate(projectSeq);
        }, 10000); // Poll every 10 second
      }
    } else {
      // Stop polling if no processing verifications
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [projectSeq, clarityData, fossidData, sbomData, clarityAirData]);

  // API calls for dropdown data
  const getLicensePolicyList = useMutationData(
    queryKeys.licensePoliciesOnly,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const licensePolicyList = normalizeResponseData(response).map(({ licensePolicySeq, policyName }) => ({
            value: licensePolicySeq,
            label: policyName,
          }));
          setLicensePolicyList(licensePolicyList);
        }
      },
      onError: (error) => {
        console.error("Error during get license policy data:", error);
      },
    },
  );

  const getDistributionScopes = useMutationData(
    queryKeys.getDistributionScopes,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setDistributionScopeList(response.map((data) => ({ value: data.code, label: data.codeName })));
        }
      },
      onError: (error) => {
        console.error("Error during get distribution scopes data:", error);
      },
    },
  );

  const mapMyProductsToOptions = (items) =>
    (items ?? []).map((item) => ({
      value: item.productSeq,
      label: item.productName,
    }));

  const extractMyProductsFromResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.object)) return response.object;
    return [];
  };

  const getMyProducts = useMutationData(
    queryKeys.myProducts,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setMyProductsList(mapMyProductsToOptions(extractMyProductsFromResponse(response)));
        }
      },
      onError: (error) => {
        console.error("Error fetching my products:", error);
      },
    },
  );

  // API call for project details
  const queryKey = [queryKeys.getProjectDetail, projectSeq];
  const queryUrl = queryKeys.getProjectDetail + "/" + projectSeq;

  const getData = useFetchData(
    queryKey,
    queryUrl,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!projectSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (getData?.error?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/projects" } });
    }
  }, [getData]);

  // API call for updating project
  const updateProject = useMutationData(
    queryKeys.updateProject + "/" + projectSeq,
    {
      method: "PUT",
    },
    {
      onSuccess: async (response) => {
        // Refresh the project data after successful update
        getData.refetch();
        setIsEditMode(false);
        setEditFormData({});
        useSuccessToast(TOAST.success, t("project.project-update-success"));
        await updateDataInIndexedDb(STORES.PROJECTS, response, response.projectSeq);
      },
      onError: (error) => {
        console.error("Error during update project:", error);
        // You might want to show an error message to the user here
      },
    },
  );

  // API call for getting members by project ID
  const getMembersByProjectId = useFetchData(
    [queryKeys.getMembersByProjectId, projectSeq, memberCurrentPage, memberItemsPerPage],
    queryKeys.getMembersByProjectId,
    {
      method: "GET",
    },
    {
      projectSeq: projectSeq,
      pageSize: memberItemsPerPage,
      startRow: memberItemsPerPage * (memberCurrentPage - 1),
    },
    {
      enabled: !!projectSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  // API call for getting all members by project ID (for filtering) - only enabled when modal is open
  // This optimization prevents unnecessary API calls when the modal is not being used
  const getAllMembersByProjectId = useFetchData(
    [queryKeys.getMembersByProjectId, projectSeq, "all"],
    queryKeys.getMembersByProjectId,
    {
      method: "GET",
    },
    {
      projectSeq: projectSeq,
      pageSize: 999,
      startRow: 0,
    },
    {
      enabled: !!projectSeq && (isMemberModalOpen || isEditMode),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const addMembersIntoProject = useMutationData(
    queryKeys.projectMembers,
    {
      method: "POST",
    },
    {
      onSuccess: () => {
        fetchMembersData(memberCurrentPage);
        getAllMembersByProjectId.refetch();
        useSuccessToast(TOAST.success, t("common.members-successfully-added"));
      },
      onError: (error) => {
        console.error("Error during add members into project:", error);
      },
    },
  );

  const deleteMembers = useMutationData(
    queryKeys.projectMembers,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        fetchMembersData(memberCurrentPage);
        getAllMembersByProjectId.refetch();
        useSuccessToast(TOAST.success, t("common.members-successfully-deleted"));
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
      },
    },
  );

  const deleteFile = useMutationData(
    (fileInfoSeq) => `file_info/${fileInfoSeq}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        useSuccessToast(TOAST.success, t("project.document.delete-success"));
      },
      onError: (error) => {
        console.error("Error during delete file:", error);
      },
    },
  );

  const getSearchCreators = useMutationData(
    queryKeys.getSearchCreatorList,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (Array.isArray(response)) {
          const mappedOptions = response.map((item) => ({
            ...item,
            value: item.createrId,
            label: item.createrName,
          }));
          setCreatorOptions(mappedOptions);
        }
      },
      onError: (error) => {
        console.error("Error fetching search info data:", error);
      },
    },
  );

  // Trigger API calls when component mounts
  useEffect(() => {
    if (isFirstRender.current) {
      getDistributionScopes.mutateAsync();
      getLicensePolicyList.mutateAsync();
      getMyProducts.mutateAsync();
      fetchSearchInfo();
      getSearchCreators.mutateAsync();
      isFirstRender.current = false;
    }
  }, []);

  // Function to fetch members data
  const fetchMembersData = (page = 1) => {
    if (projectSeq) {
      // Invalidate the specific query cache and then refetch
      queryClient.invalidateQueries({
        queryKey: [queryKeys.getMembersByProjectId, projectSeq, memberCurrentPage, memberItemsPerPage]
      });
    }
  };

  // Handle members data response
  useEffect(() => {
    if (getMembersByProjectId.data && !getMembersByProjectId.isLoading && !getMembersByProjectId.error) {
      const response = getMembersByProjectId.data;
      if (typeof response !== "string" && response && response.object && Array.isArray(response.object)) {
        const mappedMembers = response.object.map((member) => ({
          projectMemberSeq: member.projectMemberSeq,
          userId: member.member?.userId,
          userName: member.member?.userName,
          deptCdName: member.member?.deptCdName || "",
          email: member.member?.email || "",
          roleCdName: member.member?.roleCdName || "",
          companyCdName: member.member?.companyCdName || "",
          position: member.member?.position || "",
          phoneNo: member.member?.phoneNo || "",
          cellNo: member.member?.cellNo || "",
          createdAt: member.createdAt || "",
          createrName: member.createrName || "",
        }));
        setMembersData(mappedMembers);
        setMemberTotalData(response.objectCount || 0);
        setMemberTotalPages(Math.ceil((response.objectCount || 0) / memberItemsPerPage));
      } else {
        // Handle case where response.object is undefined, null, or not an array
        setMembersData([]);
        setMemberTotalData(0);
        setMemberTotalPages(1);
      }
    }
  }, [getMembersByProjectId.data, getMembersByProjectId.isLoading, getMembersByProjectId.error]);

  // Handle all members data response for filtering
  useEffect(() => {
    if (getAllMembersByProjectId.data && !getAllMembersByProjectId.isLoading && !getAllMembersByProjectId.error) {
      const response = getAllMembersByProjectId.data;
      if (typeof response !== "string" && response && response.object && Array.isArray(response.object)) {
        const mappedAllMembers = response.object.map((member) => ({
          projectMemberSeq: member.projectMemberSeq,
          userId: member.member?.userId,
          userName: member.member?.userName,
          deptCdName: member.member?.deptCdName || "",
          email: member.member?.email || "",
          roleCdName: member.member?.roleCdName || "",
          companyCdName: member.member?.companyCdName || "",
          position: member.member?.position || "",
          phoneNo: member.member?.phoneNo || "",
          cellNo: member.member?.cellNo || "",
          createdAt: member.createdAt || "",
          createrName: member.createrName || "",
        }));
        setAllMembersInProject(mappedAllMembers);
      } else {
        setAllMembersInProject([]);
      }
    }
  }, [getAllMembersByProjectId.data, getAllMembersByProjectId.isLoading, getAllMembersByProjectId.error]);

  // Process project data when it's available
  useEffect(() => {
    if (getData.data && !getData.isLoading && !getData.error) {
      const response = getData.data;
      if (typeof response !== "string") {

        setProjectData(response);

        if (response.fileGroupNo) {
          setFileGroupNo(response.fileGroupNo);
        }
      }
    }
  }, [getData.data, getData.isLoading, getData.error]);

  // Update fileInfoModified when fileInfo changes
  useEffect(() => {
    setFileInfoModified(fileInfo);
  }, [fileInfo]);

  // Use fetched data only
  const displayProjectData = getData.data || projectData;

  const {
    canEditProjectManagerField,
    canEditProjectProductNameField,
    canAddProjectMemberPermission,
    canDeleteProjectMemberPermission,
    canEditProjectInformationPermission,
  } = useProjectDetailPermissions(displayProjectData);

  const effectiveProjectManagerId =
    isEditMode && canEditProjectManagerField
      ? editFormData.projectManagerId || null
      : displayProjectData?.projectManagerId || null;

  const filteredProjectManagerList = useMemo(() => {
    if (!projectManagerList?.length) return [];
    const memberIds = new Set(
      allMembersInProject.map((member) => member.userId).filter(Boolean),
    );
    return projectManagerList.filter((option) => !memberIds.has(option.value));
  }, [projectManagerList, allMembersInProject]);

  const handleOpenMemberModal = () => {
    if (!canAddProjectMemberPermission) return;
    setIsMemberModalOpen(true);
  };

  // Update editFormData.productSeq when myProductsList is loaded and we're in edit mode
  useEffect(() => {
    if (isEditMode && displayProjectData && myProductsList.length > 0) {
      const selectedProduct = findMatchingProduct(displayProjectData, myProductsList);

      if (selectedProduct) {
        setEditFormData(prev => ({
          ...prev,
          productSeq: selectedProduct
        }));
      }
    }
  }, [myProductsList, isEditMode, displayProjectData]);

  const hasAnyVerification = (clarityData && clarityData.length > 0) ||
    (fossidData && fossidData.length > 0) ||
    (sbomData && sbomData.length > 0) ||
    (clarityAirData && clarityAirData.length > 0);

  // Helper function to find matching product
  const findMatchingProduct = (projectData, productOptions) => {
    if (!projectData || !productOptions || productOptions.length === 0) {
      return null;
    }

    let matchingProduct = null;

    if (projectData.product) {
      if (projectData.product.productSeq) {
        matchingProduct = productOptions.find(p =>
          parseInt(p.value) === parseInt(projectData.product.productSeq) ||
          p.value === projectData.product.productSeq ||
          String(p.value) === String(projectData.product.productSeq)
        );
      }
    }
    return matchingProduct ? { value: matchingProduct.value, label: matchingProduct.label } : null;
  };

  // Handle edit mode functions
  const handleEditClick = () => {
    if (!canEditProjectInformationPermission) return;
    setIsEditMode(true);

    const selectedProduct = findMatchingProduct(displayProjectData, myProductsList);

    setEditFormData({
      projectId: displayProjectData.projectId || "",
      projectName: displayProjectData.projectName || "",
      productSeq: selectedProduct,
      projectManagerId: displayProjectData.projectManagerId || "",
      projectManagerName: displayProjectData.projectManagerName || "",
      assetClassificationName: displayProjectData.assetClassificationName || "",
      assetClassificationSeq: displayProjectData.assetClassificationSeq || null,
      createdAt: displayProjectData.createdAt || "",
      completionRequestDt: displayProjectData.completionRequestDt || "",
      createrName: displayProjectData.createrName || "",
      createrDeptCdName: displayProjectData.createrDeptCdName || "",
      distributionScopeCd: displayProjectData.distributionScopeCd || null,
      distributionScopeCdName: displayProjectData.distributionScopeCdName || "",
      updaterName: displayProjectData.updaterName || "",
      updatedAt: displayProjectData.updatedAt || "",
      licenseClassCdName: displayProjectData.licenseClassCdName || "",
      licenseName: displayProjectData.licenseName || "",
      licensePolicySeq: displayProjectData.licensePolicySeq || null,
      licenseIdentifier: displayProjectData.licenseIdentifier || "",
      remark: displayProjectData.remark || "",
    });
    setSelectedLicense({
      licenseIdentifier: displayProjectData.licenseIdentifier || "",
      licenseName: displayProjectData.licenseName || "",
    });
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
    setSelectedLicense(null);
    setValidationErrors({});
  };

  const handleSaveEdit = () => {
    if (!canEditProjectInformationPermission) return;
    // Validate required fields
    const errors = {};

    if (!editFormData.projectName || editFormData.projectName.trim() === '') {
      errors.projectName = t("project.project-name-required");
    }

    if (canEditProjectProductNameField && !editFormData.productSeq) {
      errors.productSeq = t("project.product-name-required");
    }

    if (!editFormData.completionRequestDt) {
      errors.completionRequestDt = t("project.completion-date-required");
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear validation errors
    setValidationErrors({});

    // Prepare the data for the API call - include all current data with edited fields
    const updateData = {
      // Include all current project data
      ...displayProjectData,
      // Override with edited fields
      projectName: editFormData.projectName,
      productSeq: canEditProjectProductNameField
        ? (editFormData.productSeq ? parseInt(editFormData.productSeq.value) : null)
        : getOriginalProductSeq(displayProjectData),
      completionRequestDt: editFormData.completionRequestDt,
      assetClassificationSeq: editFormData.assetClassificationSeq,
      distributionScopeCd: editFormData.distributionScopeCd,
      licensePolicySeq: editFormData.licensePolicySeq,
      licenseName: editFormData.licenseName,
      licenseIdentifier: editFormData.licenseIdentifier,
      projectManagerId: canEditProjectManagerField
        ? editFormData.projectManagerId
        : displayProjectData?.projectManagerId,
      remark: editFormData.remark,
    };

    // Call the API to update the project
    updateProject.mutate(updateData);
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for required fields when user starts typing
    if (validationErrors[field] && ['projectName', 'productSeq', 'completionRequestDt'].includes(field)) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleLicenseSelect = (selectedLicense) => {
    setSelectedLicense(selectedLicense);
    if (selectedLicense) {
      setEditFormData((prev) => ({
        ...prev,
        licenseName: selectedLicense.licenseName,
        licenseIdentifier: selectedLicense.licenseIdentifier,
      }));
    } else {
      setEditFormData((prev) => ({
        ...prev,
        licenseName: "",
        licenseIdentifier: "",
      }));
    }

    // Clear validation error for license policy when license is selected
    if (validationErrors.licensePolicySeq) {
      setValidationErrors((prev) => ({
        ...prev,
        licensePolicySeq: undefined,
      }));
    }
  };

  // Helper function to translate scan type
  const translateScanType = (scanTypeValue) => {
    const scanTypeMap = {
      "Binary": t("project.scan-type-status.binary"),
      "Source Code": t("project.scan-type-status.source-code"),
    };
    return scanTypeMap[scanTypeValue] || scanTypeValue;
  };

  const renderVerificationStatusCell = (status, row = null, processingRowId = null) => {
    const statusStyles = {
      ON_ANALYSIS: colors.textStatusAnalysis,
      ANALYSIS_CHECK: colors.textStatusAnalysis,
      REQUEST: colors.textStatusRequest,
      ON_VERIFICATION: colors.textStatusVerification,
      ON_REVIEW: colors.textStatusReview,
      STANDBY_FOLLOWUP: colors.textStatusFollowup,
      FOLLOWUP_CHECK: colors.textStatusFollowup,
      DONE: colors.textStatusSuccess,
      REVERIFICATION: colors.textStatusReverification,
      ERROR: colors.textStatusAnalysis,
    };

    const statusColorDot = {
      ON_ANALYSIS: colors.bgStatusAnalysis,
      ANALYSIS_CHECK: colors.bgStatusAnalysis,
      REQUEST: colors.bgStatusRequest,
      ON_VERIFICATION: colors.bgStatusVerification,
      ON_REVIEW: colors.bgStatusReview,
      STANDBY_FOLLOWUP: colors.bgStatusFollowup,
      FOLLOWUP_CHECK: colors.bgStatusFollowup,
      DONE: colors.bgStatusSuccess,
      REVERIFICATION: colors.bgStatusReverification,
      ERROR: colors.bgStatusAnalysis,
    };

    const statusText = {
      ON_ANALYSIS: t("status.analyzing"),
      ANALYSIS_CHECK: t("status.analysisCheck"),
      REQUEST: t("status.request"),
      ON_VERIFICATION: t("status.onVerification"),
      ON_REVIEW: t("status.onReview"),
      STANDBY_FOLLOWUP: t("status.standbyFollowup"),
      FOLLOWUP_CHECK: t("status.followupCheck"),
      DONE: t("status.done"),
      REVERIFICATION: t("status.reverification"),
      ERROR: t("status.error"),
    };

    // For ON_VERIFICATION status, use the specific status name if available
    let displayText = statusText[status];
    if (status === "ON_VERIFICATION" && row.verificationStatusCdName) {
      // Map the status name to translation keys
      const statusNameMap = {
        "Usage Type Review": t("status.usageTypeReview"),
        "Usage Type Check": t("status.usageTypeCheck"),
        "Usage Type Input": t("status.usageTypeInput"),
      };

      displayText = statusNameMap[row.verificationStatusCdName] || row.verificationStatusCdName;
    }

    // Check if this row should blink (for FossID and Clarity verifications only)
    const shouldBlink =
      row &&
      (
        // Blink if this row is currently being processed (new verification)
        // Also blink if verification is in processing status (including re-verification)
        (['ON_ANALYSIS'].includes(row.verificationStatusCd))
      );

    return (
      <div className={`${shouldBlink ? "fossid-blinking-row" : ""} relative`}>
        <span
          className={`inline-flex items-center py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
        >
          <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
          {displayText}
        </span>
      </div>
    );
  };

  const renderToolScanStatusCell = (status, row = null, processingRowId = null) => {
    const statusStyles = {
      Success: colors.importStatusCreated,
      Scanning: colors.importStatusInProgress,
      Fail: colors.importStatusFailed,
    };

    // Helper function to translate status
    const translateStatus = (statusValue) => {
      const statusMap = {
        "Success": t("project.status.success"),
        "Scanning": t("project.status.scanning"),
        "Fail": t("project.status.fail"),
      };
      return statusMap[statusValue] || statusValue;
    };


    // If status is null, undefined, or empty string, set it to "Scanning"
    const displayStatus = !status || status === "" ? "Scanning" : status;

    // Check if this row should blink for tool scan status
    const shouldBlink =
      row &&
      (
        // Blink if this row is currently being processed (new verification)
        // Also blink if verification is in processing status (including re-verification)
        (['ON_ANALYSIS'].includes(row.verificationStatusCd))
      );

    return (
      <div className={`${shouldBlink ? "fossid-blinking-row" : ""} relative`}>
        <span
          className={`inline-flex items-center rounded-2xl px-2 py-1 text-sm leading-[16px] font-semibold ${statusStyles[displayStatus] || colors.badgeVariantDefault}`}
        >
          {translateStatus(displayStatus)}
        </span>
      </div>
    );
  };

  const renderTagsCell = (tags) => {
    if (!Array.isArray(tags) || tags.length === 0) {
      return <span className={colors.textMuted}>-</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag, index) => (
          <span
            key={index}
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.badgeVariantPrimary}`}
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  // Member management functions

  const handleMemberSelect = (selectedMembers) => {
    if (!canAddProjectMemberPermission) return;
    if (!projectData?.projectSeq || !projectData?.projectId) return;
    // Avoid posting before existing members are loaded (would bypass local dedupe).
    if (!getAllMembersByProjectId.data && (getAllMembersByProjectId.isLoading || getAllMembersByProjectId.isFetching)) {
      return;
    }

    // Filter out members that are already in the project using allMembersInProject
    const existingUserIds = allMembersInProject.map(member => member.userId);
    const newMembers = selectedMembers.filter(member => !existingUserIds.includes(member.userId));

    if (newMembers.length === 0 && (existingUserIds.length == selectedMembers.length)) {
      // All selected members are already in the project
      useSuccessToast(TOAST.info, t("common.all-selected-members-exist"), "");
      return;
    }

    if (newMembers.length > 0) {
      const newMemberIds = new Set(newMembers.map((member) => member.userId).filter(Boolean));

      if (isEditMode && canEditProjectManagerField && newMemberIds.has(editFormData.projectManagerId)) {
        setEditFormData((prev) => ({
          ...prev,
          projectManagerId: "",
          projectManagerName: "",
        }));
      }

      const newProjectMembers = newMembers.map((member) => ({
        projectSeq: projectData.projectSeq,
        projectId: projectData.projectId,
        memberId: member.userId
      }));

      addMembersIntoProject.mutate(newProjectMembers);
    }
  };

  // Handle removing members when they are unchecked in the modal
  const handleRemoveMember = (membersToRemove) => {
    if (!canDeleteProjectMemberPermission) return;
    if (!projectData?.projectSeq || !projectData?.projectId) return;

    // Get projectMemberSeq for the members to remove
    const membersToDelete = membersToRemove.map(member => {
      const existingMember = allMembersInProject.find(existing => existing.userId === member.userId);
      return existingMember;
    }).filter(Boolean);

    if (membersToDelete.length > 0) {
      // Use the existing delete API with onSuccess callback to refresh data
      deleteMembers.mutate(membersToDelete.map((member) => member.projectMemberSeq), {
        onSuccess: () => {
          // Calculate if current page will be empty after deletion
          const remainingMembersOnCurrentPage = membersData.length - membersToDelete.length;

          // If current page will be empty and there are other pages, go to previous page
          if (remainingMembersOnCurrentPage <= 0 && memberCurrentPage > 1) {
            setMemberCurrentPage(prev => Math.max(1, prev - 1));
          }
        },
        onError: (error) => {
          console.error("Error during remove members:", error);
        },
      });
    }
  };

  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null); // single or array
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  // Update handleDeleteMember to show modal for single delete
  const handleDeleteMember = (memberToDelete) => {
    if (!canDeleteProjectMemberPermission) return;
    setMemberToDelete([memberToDelete]);
    setShowDeleteMemberModal(true);
  };

  // Update handleDeleteSelectedMembers to show modal for multiple delete
  const handleDeleteSelectedMembers = () => {
    if (!canDeleteProjectMemberPermission) return;
    setMemberToDelete(membersData.filter((member) => memberSelectedRows.includes(member.userId)));
    setShowDeleteMemberModal(true);
  };

  // Confirm delete handler
  const handleConfirmDeleteMember = () => {
    if (!canDeleteProjectMemberPermission) return;
    if (!projectData?.projectSeq || !projectData?.projectId) return;
    setDeleteLoading(true);
    deleteMembers.mutate(memberToDelete.map((member) => member.projectMemberSeq), {
      onSuccess: () => {
        // Calculate if current page will be empty after deletion
        const remainingMembersOnCurrentPage = membersData.length - memberToDelete.length;

        // If current page will be empty and there are other pages, go to previous page
        if (remainingMembersOnCurrentPage <= 0 && memberCurrentPage > 1) {
          setMemberCurrentPage(prev => Math.max(1, prev - 1));
        }
        setMemberSelectedRows([]);
      },
      onSettled: () => {
        setDeleteLoading(false);
        setShowDeleteMemberModal(false);
        setMemberToDelete(null);
      },
    });
  };

  // Document management functions
  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    deleteFile.mutate(documentToDelete.fileInfoSeq, {
      onSuccess: () => {
        const newTotalData = documentTotalData - 1;
        const newTotalPages = Math.ceil(newTotalData / documentItemsPerPage);
        let nextPage = documentCurrentPage;
        if (documentCurrentPage > newTotalPages) {
          nextPage = newTotalPages === 0 ? 1 : newTotalPages;
          setDocumentCurrentPage(nextPage);
        }
        getFileInfoMutation.mutate({
          fileGroupNo,
          page: nextPage,
          pageSize: documentItemsPerPage,
        });
      },
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";

    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0 && projectSeq) {
        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", projectSeq);
        formData.append("targetTypeCd", "PROJECT");
        formData.append("fileGroupNo", projectData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        uploadFiles.mutate(formData, {
          onSuccess: (response) => {
            if (response?.fileGroupNo && response.fileGroupNo !== projectData.fileGroupNo) {
              setFileGroupNo(response.fileGroupNo);
              setProjectData({ ...projectData, fileGroupNo: response.fileGroupNo });
            }
            getFileInfoMutation.mutate({ fileGroupNo: response?.fileGroupNo || fileGroupNo, page: documentCurrentPage, pageSize: documentItemsPerPage });
          },
        });
      }
    };

    fileInput.click();
  };

  // Click handler for clarity verification items
  const handleClarityItemClick = (clarityItem) => {
    navigate(`/projects/${projectSeq}/clarity/${clarityItem.binaryVerificationSeq}`);
  };

  const handleFossidItemClick = (fossidItem) => {
    navigate(`/projects/${projectSeq}/fossid/${fossidItem.sourceCodeVerificationSeq}`);
  };

  const handleSbomItemClick = (sbomItem) => {
    navigate(`/projects/${projectSeq}/sbom/${sbomItem.sbomVerificationSeq}`);
  };

  const handleClarityAirItemClick = (clarityAirItem) => {
    navigate(`/projects/${projectSeq}/clarity-air/${clarityAirItem.clarityAirVerificationSeq}`);
  };

  const renderVerificationCell = (row, column) => {
    const cellContent = (() => {
      switch (column.key) {
        case "clarityScanStatusCdName":
          return renderToolScanStatusCell(row[column.key], row, processingRowId);
        case "fossidScanStatusCdName":
          return renderToolScanStatusCell(row[column.key], row, processingRowId);
        case "sbomScanStatusCdName":
          return renderToolScanStatusCell(row[column.key], row, processingRowId);
        case "clarityAirScanStatusCdName":
          return renderToolScanStatusCell(row[column.key], row, processingRowId);
        case "verificationStatusCd":
          return renderVerificationStatusCell(row[column.key], row, processingRowId);
        case "tags":
          return Array.isArray(row[column.key]) ? renderTagsCell(row[column.key]) : row[column.key];
        default:
          return row[column.key];
      }
    })();

    return cellContent;
  };

  // Tab configurations
  const projectInfoTabs = [
    {
      label: t("project.general-information"),
      content: (
        <div className="relative" data-testid={`${TEST_ID}-general-info`}>
          {/* Header with title and Edit/Save/Cancel buttons */}
          <div className="flex items-center justify-between pb-4" data-testid={`${TEST_ID}-general-info-header`}>
            <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${TEST_ID}-general-info-title`}>
              {t("project.general-information")}
            </h3>
            {isEditMode ? (
              <div className="flex space-x-3" data-testid={`${TEST_ID}-edit-actions`}>
                <OutlineButton data-testid={`${TEST_ID}-cancel-button`} onClick={handleCancelEdit}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid={`${TEST_ID}-save-button`}
                  onClick={handleSaveEdit}
                  disabled={updateProject.isPending}
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </div>
            ) : (
              canEditProjectInformationPermission && (
                <OutlineButton data-testid={`${TEST_ID}-edit-button`} onClick={handleEditClick}>{t("project.edit-information")}</OutlineButton>
              )
            )}
          </div>

          {/* Required fields note */}
          {isEditMode && (
            <div className="mb-4 px-4" data-testid={`${TEST_ID}-required-fields-note`}>
              <p className={`text-xs ${colors.textSecondary}`}>
                {t("project.required-fields-note")}
              </p>
            </div>
          )}

          {/* Content Grid */}
          <div className="space-y-2 px-4">
            {/* Row 1 */}
            <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-t ${colors.borderSection} pt-3 mb-4`} data-testid={`${TEST_ID}-general-info-row-1`}>
              <div data-testid={fieldTestId("project-id")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.project-id")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("project-id")}
                    type="text"
                    value={editFormData.projectId || ""}
                    onChange={(e) => handleInputChange("projectId", e.target.value)}
                    disabled
                  />
                ) : (
                  <p
                    data-testid={valueTestId("project-id")}
                    title={displayProjectData?.projectId}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate line-clamp-2 ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isLoading || !displayProjectData ? "" : displayProjectData.projectId}
                  </p>
                )}
              </div>
              <div data-testid={fieldTestId("project-name")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.project-name")}
                  {isEditMode && (<span className={`ml-1 ${colors.textRequired}`}>*</span>)}
                </label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("project-name")}
                    type="text"
                    value={editFormData.projectName || ""}
                    onChange={(e) => handleInputChange("projectName", e.target.value)}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                ) : (
                  <div
                    data-testid={valueTestId("project-name")}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    title={displayProjectData?.projectName}
                  >
                    {getData.isLoading || !displayProjectData ? "" : displayProjectData.projectName}
                  </div>
                )}
                {validationErrors.projectName && (
                  <p className={`${colors.textDanger} text-xs mt-1`} data-testid={`${TEST_ID}-error-project-name`}>{validationErrors.projectName}</p>
                )}
              </div>
              <div data-testid={fieldTestId("project-manager")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.project-manager")}</label>
                {isEditMode ? (
                  canEditProjectManagerField ? (
                    <CustomSelect
                      data-testid={fieldTestId("project-manager")}
                      options={filteredProjectManagerList || []}
                      value={filteredProjectManagerList?.find((opt) => opt.value === editFormData.projectManagerId) || null}
                      onChange={(selectedOption) => handleInputChange("projectManagerId", selectedOption?.value || "")}
                      placeholder={t("common.please-select")}
                      isSearchable={true}
                      isClearable={true}
                      displayFormatLabelWithID={true}
                      className="w-full text-sm font-medium"
                    />
                  ) : (
                    <CustomInput
                      data-testid={fieldTestId("project-manager")}
                      type="text"
                      value={formatUserNameWithId(
                        editFormData.projectManagerName || displayProjectData?.projectManagerName,
                        editFormData.projectManagerId || displayProjectData?.projectManagerId
                      )}
                      onChange={(e) => handleInputChange("projectManagerName", e.target.value)}
                      disabled
                    />
                  )
                ) : (
                  <div
                    data-testid={valueTestId("project-manager")}
                    className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {!(getData.isLoading || !displayProjectData) && (
                      <UserNameWithId
                        name={displayProjectData.projectManagerName}
                        id={displayProjectData.projectManagerId}
                        layout="inline"
                      />
                    )}
                  </div>
                )}
              </div>
              <div data-testid={fieldTestId("product-name")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.product-name")}
                  {isEditMode && canEditProjectProductNameField && (<span className={`ml-1 ${colors.textRequired}`}>*</span>)}
                </label>
                {isEditMode ? (
                  canEditProjectProductNameField ? (
                    <CustomSelect
                      data-testid={fieldTestId("product-name")}
                      options={myProductsList}
                      value={editFormData.productSeq}
                      onChange={(selectedOption) => handleInputChange("productSeq", selectedOption)}
                      placeholder={t("common.please-select")}
                      className="w-full"
                      isSearchable={true}
                      isClearable={true}
                    />
                  ) : (
                    <CustomInput
                      data-testid={fieldTestId("product-name")}
                      type="text"
                      value={
                        editFormData.productSeq?.label
                        || displayProjectData?.product?.productName
                        || ""
                      }
                      onChange={(e) => handleInputChange("productSeq", e.target.value)}
                      disabled
                    />
                  )
                ) : (
                  <div data-testid={valueTestId("product-name")} className={`min-h-10 flex items-center ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}>
                    <span className={`text-sm ${colors.textPrimary} truncate`} title={renderProductNameDisplay()}>
                      {renderProductNameDisplay()}
                    </span>
                  </div>
                )}
                {validationErrors.productSeq && (
                  <p className={`${colors.textDanger} text-xs mt-1`} data-testid={`${TEST_ID}-error-product-name`}>{validationErrors.productSeq}</p>
                )}
              </div>
              <div data-testid={fieldTestId("asset-classification")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.asset-classification")}
                </label>
                {isEditMode ? (
                  <CustomSelect
                    data-testid={fieldTestId("asset-classification")}
                    options={assetClassificationsList}
                    value={assetClassificationsList.find(
                      (opt) => opt.value === editFormData.assetClassificationSeq,
                    )}
                    onChange={(opt) => handleInputChange("assetClassificationSeq", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    className="w-full"
                    isSearchable={false}
                    clearable={false}
                    disabled={hasAnyVerification}
                  />
                ) : (
                  <p
                    data-testid={valueTestId("asset-classification")}
                    title={displayProjectData?.assetClassificationName}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isLoading || !displayProjectData ? "" : displayProjectData.assetClassificationName || ""}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2 */}
            <div
              data-testid={`${TEST_ID}-general-info-row-2`}
              className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}
            >
              <div data-testid={fieldTestId("creation-date")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.creation-date")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("creation-date")}
                    type="text"
                    value={editFormData.createdAt ? formatDateTime(editFormData.createdAt) : ""}
                    onChange={(e) => handleInputChange("createdAt", e.target.value)}
                    disabled
                  />
                ) : (
                  <p
                    data-testid={valueTestId("creation-date")}
                    title={formatDateTime(displayProjectData?.createdAt)}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isLoading || !displayProjectData
                      ? ""
                      : displayProjectData.createdAt
                        ? formatDateTime(displayProjectData.createdAt)
                        : ""}
                  </p>
                )}
              </div>
              <div className="relative" data-testid={fieldTestId("completion-request-date")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.completion-request-date")}
                  {isEditMode && (<span className={`ml-1 ${colors.textRequired}`}>*</span>)}
                </label>
                {isEditMode ? (
                  <CustomDatePicker
                    data-testid={fieldTestId("completion-request-date")}
                    selected={editFormData.completionRequestDt ? new Date(editFormData.completionRequestDt) : null}
                    onChange={(date) => handleInputChange("completionRequestDt", date)}
                    placeholderText={t("project.select-date-placeholder")}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                ) : (
                  <p
                    data-testid={valueTestId("completion-request-date")}
                    title={formatDateTime(displayProjectData?.completionRequestDt)}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isLoading || !displayProjectData
                      ? ""
                      : displayProjectData.completionRequestDt
                        ? formatDate(displayProjectData.completionRequestDt)
                        : ""}
                  </p>
                )}
                {validationErrors.completionRequestDt && (
                  <p className={`${colors.textDanger} text-xs mt-1`} data-testid={`${TEST_ID}-error-completion-request-date`}>{validationErrors.completionRequestDt}</p>
                )}
              </div>
              <div data-testid={fieldTestId("creator")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.creator")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("creator")}
                    type="text"
                    value={formatUserNameWithId(editFormData.createrName || displayProjectData?.createrName, displayProjectData?.createrId)}
                    onChange={(e) => handleInputChange("createrName", e.target.value)}
                    disabled
                  />
                ) : (
                  <div
                    data-testid={valueTestId("creator")}
                    className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getData.isLoading || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {!(getData.isLoading || !displayProjectData) && (
                      <UserNameWithId name={displayProjectData.createrName} id={displayProjectData.createrId} layout="inline" />
                    )}
                  </div>
                )}
              </div>
              {/* <div data-testid={fieldTestId("department")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("common.department")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("department")}
                    type="text"
                    value={translateDepartment(t, editFormData.createrDeptCdName) || ""}
                    onChange={(e) => handleInputChange("createrDeptCdName", e.target.value)}
                    disabled
                  />
                ) : (
                  <p
                    data-testid={valueTestId("department")}
                    title={translateDepartment(t, displayProjectData?.createrDeptCdName)}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isPending || !displayProjectData ? "" : translateDepartment(t, displayProjectData.createrDeptCdName) || ""}
                  </p>
                )}
              </div> */}
              <div data-testid={fieldTestId("distribution-scope")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.distribution-scope")}
                </label>
                {isEditMode ? (
                  <CustomSelect
                    data-testid={fieldTestId("distribution-scope")}
                    options={distributionScopeList}
                    value={distributionScopeList.find((opt) => opt.value === editFormData.distributionScopeCd)}
                    onChange={(opt) => handleInputChange("distributionScopeCd", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    className="w-full"
                    isSearchable={false}
                    clearable={false}
                  />
                ) : (
                  <p
                    data-testid={valueTestId("distribution-scope")}
                    title={displayProjectData?.distributionScopeCdName}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isPending || !displayProjectData ? "" : displayProjectData.distributionScopeCdName || ""}
                  </p>
                )}
              </div>
              <div data-testid={fieldTestId("last-edited")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("common.last-modified-by")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("last-edited")}
                    type="text"
                    value={formatUserNameWithId(editFormData.updaterName || displayProjectData?.updaterName, displayProjectData?.updaterId)}
                    onChange={(e) => handleInputChange("updaterName", e.target.value)}
                    disabled
                  />
                ) : (
                  <div
                    data-testid={valueTestId("last-edited")}
                    className={`min-h-10 flex min-w-0 w-full items-center overflow-hidden ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {!(getData.isPending || !displayProjectData) && (
                      <UserNameWithId name={displayProjectData.updaterName} id={displayProjectData.updaterId} layout="inline" />
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Row 3 */}
            <div
              data-testid={`${TEST_ID}-general-info-row-3`}
              className={`grid grid-cols-5 gap-x-4 gap-y-4 pt-3 ${!isEditMode ? `border-t ${colors.borderSection}` : ""}`}
            >
              <div data-testid={fieldTestId("last-modified")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("common.last-modified-date")}</label>
                {isEditMode ? (
                  <CustomInput
                    data-testid={fieldTestId("last-modified")}
                    type="text"
                    value={editFormData.updatedAt ? formatDateTime(editFormData.updatedAt) : ""}
                    onChange={(e) => handleInputChange("updatedAt", e.target.value)}
                    disabled
                  />
                ) : (
                  <p
                    data-testid={valueTestId("last-modified")}
                    title={formatDateTime(displayProjectData?.updatedAt)}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isPending || !displayProjectData
                      ? ""
                      : displayProjectData.updatedAt
                        ? formatDateTime(displayProjectData.updatedAt)
                        : ""}
                  </p>
                )}
              </div>
              <div data-testid={fieldTestId("license")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.license")}</label>
                {isEditMode ? (
                  <div className="relative" data-testid={`${fieldTestId("license")}-picker`}>
                    <CustomInput
                      data-testid={fieldTestId("license")}
                      type="text"
                      placeholder={t("project.select-license-placeholder")}
                      value={selectedLicense?.licenseName || editFormData.licenseName || ""}
                      readOnly
                      onClick={() => setModalSelectLicenseOpen(true)}
                      className="cursor-pointer pr-8"
                    />
                    <MagnifyingGlassIcon
                      data-testid={`${fieldTestId("license")}-search-icon`}
                      className={`absolute top-2.5 right-2 h-4 w-4 ${colors.iconMuted}`}
                    />
                  </div>
                ) : (
                  <p
                    data-testid={valueTestId("license")}
                    title={displayProjectData?.licenseName || displayProjectData?.licenseClassCdName}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isPending || !displayProjectData
                      ? ""
                      : displayProjectData.licenseName || displayProjectData.licenseClassCdName || ""}
                  </p>
                )}
              </div>
              <div data-testid={fieldTestId("license-policy")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                  {t("project.license-policy")}
                </label>
                {isEditMode ? (
                  <CustomSelect
                    data-testid={fieldTestId("license-policy")}
                    options={licensePolicyList}
                    value={licensePolicyList.find((opt) => opt.value === editFormData.licensePolicySeq)}
                    onChange={(opt) => handleInputChange("licensePolicySeq", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    className="w-full"
                    isSearchable={false}
                    clearable={false}
                  />
                ) : (
                  <p
                    data-testid={valueTestId("license-policy")}
                    title={licensePolicyList.find(policy => policy.value === displayProjectData?.licensePolicySeq)?.label}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {getData.isPending || !displayProjectData ? "" : licensePolicyList.find(policy => policy.value === displayProjectData.licensePolicySeq)?.label || ""}
                  </p>
                )}
              </div>
              <div className="col-span-2" data-testid={fieldTestId("remark")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>{t("project.remark")}</label>
                {isEditMode ? (
                  <div className="mb-0.5">
                    <CustomInput
                      data-testid={fieldTestId("remark")}
                      type="text"
                      value={editFormData.remark || ""}
                      onChange={(e) => handleInputChange("remark", e.target.value)}
                      placeholder={t("project.remark-placeholder")}
                      maxLength={CHARACTER_LENGTH.REMARK}
                    />
                    <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={valueTestId("remark-char-count")}>
                      {(editFormData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                    </p>
                  </div>
                ) : (
                  <div
                    data-testid={valueTestId("remark")}
                    className={`text-sm ${colors.textPrimary} h-10 content-center truncate ${getData.isPending || !displayProjectData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    title={displayProjectData?.remark || "---"}
                  >
                    {getData.isLoading || !displayProjectData ? "" : (displayProjectData.remark || "---")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      label: `${t("project.members")} (${memberTotalData})`,
      content: (() => {
        // const columns = ["userId", "userName", "roleCdName", "deptCdName", "email"];
        const columns = ["userId", "userName", "roleCdName", "email"];
        const columnConfig = {
          userId: {
            style: {
              width: "10%",
            },
            headerRender: () => t("project.member.user-id"),
          },
          userName: {
            headerRender: () => t("project.member.user-name"),
          },
          roleCdName: {
            headerRender: () => t("project.member.role"),
          },
          // deptCdName: {
          //   headerRender: () => t("project.member.department"),
          //   customRender: (value) => translateDepartment(t, value)
          // },
          email: {
            headerRender: () => t("project.member.email"),
          },
        };

        const handleMemberCheckboxChange = (newSelectedRows) => {
          setMemberSelectedRows([...newSelectedRows]);
        };

        return (
          <div data-testid={`${TEST_ID}-members`}>
            {/* Header with title and buttons */}
            <div className="flex items-center justify-between pb-4" data-testid={`${TEST_ID}-members-header`}>
              <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${TEST_ID}-members-title`}>{`${t("project.members")} (${memberTotalData})`}</h3>
              <div className="flex space-x-3" data-testid={`${TEST_ID}-members-actions`}>
                {canDeleteProjectMemberPermission && (
                  <DeleteButton data-testid={`${TEST_ID}-delete-members-button`} onClick={handleDeleteSelectedMembers} size="md" disabled={memberSelectedRows.length === 0}>
                    {t("project.delete-member-project-detail")}
                  </DeleteButton>
                )}
                {canAddProjectMemberPermission && (
                  <OutlineButton data-testid={`${TEST_ID}-add-member-button`} onClick={handleOpenMemberModal}>{t("project.add-member-project-detail")}</OutlineButton>
                )}
              </div>
            </div>

            <TableWithPagination
              dataTestId={`${TEST_ID}-members-table`}
              columns={columns}
              columnConfig={columnConfig}
              data={membersData}
              onRowClick={() => { }}
              loading={getMembersByProjectId.isLoading}
              totalPages={memberTotalPages}
              totalData={memberTotalData}
              currentPage={memberCurrentPage}
              setCurrentPage={setMemberCurrentPage}
              maxRowsPerPage={memberItemsPerPage}
              selectedRows={memberSelectedRows}
              onCheckboxChange={handleMemberCheckboxChange}
              onDeleteRow={canDeleteProjectMemberPermission ? handleDeleteMember : undefined}
              disabledRow={[]}
              hasCheckbox={canDeleteProjectMemberPermission}
              hasDeleteColumn={canDeleteProjectMemberPermission}
              hasRadioButton={false}
              rowKey="userId"
              shouldShowGoToPage={true}
            />
          </div>
        );
      })(),
    },
    {
      label: `${t("project.document-info")} (${documentTotalData || 0})`,
      content: (() => {

        // Fetch paginated data when page or page size changes
        useEffect(() => {
          if (fileGroupNo) {
            getFileInfoMutation.mutate({ fileGroupNo, page: documentCurrentPage, pageSize: documentItemsPerPage });
          }
        }, [fileGroupNo, documentCurrentPage, documentItemsPerPage]);

        // Reset to first page when fileGroupNo changes (not when total data changes)
        useEffect(() => {
          setDocumentCurrentPage(1);
        }, [fileGroupNo]);

        return (
          <div data-testid={`${TEST_ID}-documents`}>
            {/* Header with title and Upload file button */}
            <div className="flex items-center justify-between pb-4" data-testid={`${TEST_ID}-documents-header`}>
              <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${TEST_ID}-documents-title`}>
                {`${t("project.document-info")} (${documentTotalData || 0})`}
              </h3>
              <OutlineButton data-testid={`${TEST_ID}-upload-file-button`} onClick={handleUploadFile}>
                {t("project.document.upload-file")}
              </OutlineButton>
            </div>
            {fileInfoModified && fileInfoModified.length > 0 ? (
              <div data-testid={`${TEST_ID}-documents-table`}>
                <DocumentTable
                  data={fileInfoModified}
                  onDelete={handleDeleteDocument}
                  currentPage={documentCurrentPage}
                  totalPages={documentTotalPages}
                  onPageChange={setDocumentCurrentPage}
                  itemsPerPage={documentItemsPerPage}
                  totalData={documentTotalData}
                />
              </div>
            ) : (
              <div className={`py-8 text-center ${colors.textSecondary}`} data-testid={`${TEST_ID}-documents-empty`}>
                <p data-testid={`${TEST_ID}-documents-empty-message`}>
                  {getFileInfoMutation.isPending ? t("project.loading-files") : t("project.no-documents-found")}
                </p>
              </div>
            )}
          </div>
        );
      })(),
    },
  ];

  // Dynamic verification tabs based on scanToolList
  const verificationTabs = (() => {
    if (!scanToolList || !Array.isArray(scanToolList) || scanToolList.length === 0) {
      return [];
    }

    return scanToolList.map((tool) => {
      const toolValue = tool.value?.toUpperCase();
      let data, loading, currentPage, setCurrentPage, onRowClick;
      let columns, columnConfig;

      // Configure tool-specific data and handlers
      switch (toolValue) {
        case "CLARITY":
          data = clarityData || [];
          loading = getClarityData.isPending;
          currentPage = currentPage;
          setCurrentPage = setCurrentPage;
          onRowClick = (row) => handleClarityItemClick(row);
          columns = [
            "verificationVersion",
            "realFileName",
            "totalFileSize",
            "clarityScanStatusCdName",
            "componentVersionCnt",
            "requesterName",
            // "requesterDeptCdName",
            "updatedAt",
            "verificationStatusCd",
          ];
          columnConfig = {
            verificationVersion: {
              style: { width: "8%" },
              headerRender: () => t("common.version"),
            },
            realFileName: {
              style: { width: "18%" },
              headerRender: () => t("common.verif-real-file-name"),
            },
            totalFileSize: {
              style: { width: "10%" },
              headerRender: () => t("project.total-file-size"),
              customRender: (value, row) => formatFileSize(value),
            },
            clarityScanStatusCdName: {
              style: { width: "12%" },
              headerRender: () => t("project.tool-scan-status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "clarityScanStatusCdName" }),
            },
            componentVersionCnt: {
              style: { width: "8%" },
              headerRender: () => t("status.component-count"),
              customRender: (value, row) => value || 0,
            },
            requesterName: {
              style: { width: "11%" },
              headerRender: () => t("common.requester"),
            },
            // requesterDeptCdName: {
            //   style: { width: "15%" },
            //   headerRender: () => t("project.requester-department"),
            //   customRender: (value) => translateDepartment(t, value),
            // },
            updatedAt: {
              style: { width: "12%" },
              headerRender: () => t("common.last-modified-date"),
              customRender: (value, row) => {
                const formattedDateTime = formatDateTime(value);
                if (!formattedDateTime || formattedDateTime === "---") {
                  return <div className={colors.textNeutralMuted}>---</div>;
                }

                // Split date and time
                const [datePart, timePart] = formattedDateTime.split(' ');

                return (
                  <div className="text-sm">
                    <div className="truncate font-medium" title={datePart}>
                      {datePart || "---"}
                    </div>
                    <div className={`truncate font-medium text-xs ${colors.textNeutralMuted}`} title={timePart}>
                      {timePart || "---"}
                    </div>
                  </div>
                );
              },
            },
            verificationStatusCd: {
              style: { width: "12%" },
              headerRender: () => t("common.verif-Status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "verificationStatusCd" }),
            },
          };
          break;

        case "FOSSID":
          data = fossidData || [];
          loading = getFossidData.isPending;
          currentPage = currentPage;
          setCurrentPage = setCurrentPage;
          onRowClick = (row) => handleFossidItemClick(row);
          columns = [
            "verificationVersion",
            "realFileName",
            "totalFileSize",
            "fossidScanStatusCdName",
            "componentVersionCnt",
            "requesterName",
            // "requesterDeptCdName",
            "updatedAt",
            "verificationStatusCd",
          ];
          columnConfig = {
            verificationVersion: {
              style: { width: "8%" },
              headerRender: () => t("common.version"),
            },
            realFileName: {
              style: { width: "22%" },
              headerRender: () => t("common.verif-real-file-name"),
            },
            totalFileSize: {
              style: { width: "10%" },
              headerRender: () => t("project.total-file-size"),
              customRender: (value, row) => formatFileSize(value),
            },
            fossidScanStatusCdName: {
              style: { width: "12%" },
              headerRender: () => t("project.tool-scan-status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "fossidScanStatusCdName" }),
            },
            componentVersionCnt: {
              style: { width: "8%" },
              headerRender: () => t("status.component-count"),
              customRender: (value, row) => value || 0,
            },
            requesterName: {
              style: { width: "12%" },
              headerRender: () => t("common.requester"),
            },
            // requesterDeptCdName: {
            //   style: { width: "12%" },
            //   headerRender: () => t("project.requester-department"),
            //   customRender: (value) => translateDepartment(t, value),
            // },
            updatedAt: {
              style: { width: "12%" },
              headerRender: () => t("common.last-modified-date"),
              customRender: (value, row) => {
                const formattedDateTime = formatDateTime(value);
                if (!formattedDateTime || formattedDateTime === "---") {
                  return <div className={colors.textNeutralMuted}>---</div>;
                }

                // Split date and time
                const [datePart, timePart] = formattedDateTime.split(' ');

                return (
                  <div className="text-sm">
                    <div className="truncate font-medium" title={datePart}>
                      {datePart || "---"}
                    </div>
                    <div className={`truncate font-medium text-xs ${colors.textNeutralMuted}`} title={timePart}>
                      {timePart || "---"}
                    </div>
                  </div>
                );
              },
            },
            verificationStatusCd: {
              style: { width: "12%" },
              headerRender: () => t("common.verif-Status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "verificationStatusCd" }),
            },
          };
          break;

        case "SBOM":
          data = sbomData || [];
          loading = getSbomData.isPending;
          currentPage = sbomCurrentPage;
          setCurrentPage = setSbomCurrentPage;
          onRowClick = (row) => handleSbomItemClick(row);
          columns = [
            "verificationVersion",
            "realFileName",
            "totalFileSize",
            "componentVersionCnt",
            "requesterName",
            // "requesterDeptCdName",
            "updatedAt",
            "verificationStatusCd",
          ];
          columnConfig = {
            verificationVersion: {
              style: { width: "8%" },
              headerRender: () => t("common.version"),
            },
            realFileName: {
              style: { width: "25%" },
              headerRender: () => t("common.verif-real-file-name"),
            },
            totalFileSize: {
              style: { width: "10%" },
              headerRender: () => t("project.total-file-size"),
              customRender: (value, row) => formatFileSize(value),
            },
            componentVersionCnt: {
              style: { width: "8%" },
              headerRender: () => t("status.component-count"),
              customRender: (value, row) => value || 0,
            },
            requesterName: {
              style: { width: "12%" },
              headerRender: () => t("common.requester"),
            },
            // requesterDeptCdName: {
            //   style: { width: "12%" },
            //   headerRender: () => t("project.requester-department"),
            //   customRender: (value) => translateDepartment(t, value),
            // },
            updatedAt: {
              style: { width: "12%" },
              headerRender: () => t("common.last-modified-date"),
              customRender: (value, row) => {
                const formattedDateTime = formatDateTime(value);
                if (!formattedDateTime || formattedDateTime === "---") {
                  return <div className={colors.textNeutralMuted}>---</div>;
                }

                // Split date and time
                const [datePart, timePart] = formattedDateTime.split(' ');

                return (
                  <div className="text-sm">
                    <div className="truncate font-medium" title={datePart}>
                      {datePart || "---"}
                    </div>
                    <div className={`truncate font-medium text-xs ${colors.textNeutralMuted}`} title={timePart}>
                      {timePart || "---"}
                    </div>
                  </div>
                );
              },
            },
            verificationStatusCd: {
              style: { width: "12%" },
              headerRender: () => t("common.verif-Status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "verificationStatusCd" }),
            },
          };
          break;

        case "CLARITYAIR":
          data = clarityAirData || [];
          loading = getClarityAirData.isPending;
          currentPage = currentPage;
          setCurrentPage = setCurrentPage;
          onRowClick = (row) => handleClarityAirItemClick(row);
          columns = [
            "verificationVersion",
            "realFileName",
            "totalFileSize",
            "clarityAirScanStatusCdName",
            "componentVersionCnt",
            "requesterName",
            // "requesterDeptCdName",
            "updatedAt",
            "verificationStatusCd",
          ];
          columnConfig = {
            verificationVersion: {
              style: { width: "8%" },
              headerRender: () => t("common.version"),
            },
            realFileName: {
              style: { width: "22%" },
              headerRender: () => t("common.verif-real-file-name"),
            },
            totalFileSize: {
              style: { width: "10%" },
              headerRender: () => t("project.total-file-size"),
              customRender: (value, row) => formatFileSize(value),
            },
            clarityAirScanStatusCdName: {
              style: { width: "12%" },
              headerRender: () => t("project.tool-scan-status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "clarityAirScanStatusCdName" }),
            },
            componentVersionCnt: {
              style: { width: "8%" },
              headerRender: () => t("status.component-count"),
              customRender: (value, row) => value || 0,
            },
            requesterName: {
              style: { width: "12%" },
              headerRender: () => t("common.requester"),
            },
            // requesterDeptCdName: {
            //   style: { width: "12%" },
            //   headerRender: () => t("project.requester-department"),
            //   customRender: (value) => translateDepartment(t, value),
            // },
            updatedAt: {
              style: { width: "12%" },
              headerRender: () => t("common.last-modified-date"),
              customRender: (value, row) => {
                const formattedDateTime = formatDateTime(value);
                if (!formattedDateTime || formattedDateTime === "---") {
                  return <div className={colors.textNeutralMuted}>---</div>;
                }

                // Split date and time
                const [datePart, timePart] = formattedDateTime.split(' ');

                return (
                  <div className="text-sm">
                    <div className="truncate font-medium" title={datePart}>
                      {datePart || "---"}
                    </div>
                    <div className={`truncate font-medium text-xs ${colors.textNeutralMuted}`} title={timePart}>
                      {timePart || "---"}
                    </div>
                  </div>
                );
              },
            },
            verificationStatusCd: {
              style: { width: "12%" },
              headerRender: () => t("common.verif-Status"),
              customRender: (value, row) => renderVerificationCell(row, { key: "verificationStatusCd" }),
            },
          };
          break;

        default:
          // For unknown tool types, return null to skip rendering
          return null;
      }

      // Return the tab configuration
      const verificationTableTestId = `${TEST_ID}-verification-table-${tool.value?.toLowerCase()}`;

      return {
        label: `${t(`dashboard.${tool.value?.toLowerCase()}`) || tool.label} (${data?.length || 0})`,
        content: (
          <div data-testid={verificationTableTestId}>
            <TableWithPagination
              dataTestId={verificationTableTestId}
              columns={columns}
              data={data}
              currentPage={currentPage}
              totalPages={Math.ceil((data?.length || 0) / 10)}
              totalData={data?.length || 0}
              setCurrentPage={setCurrentPage}
              maxRowsPerPage={10}
              loading={loading}
              columnConfig={columnConfig}
              onRowClick={onRowClick}
              showPagination={false}
            />
          </div>
        ),
      };
    }).filter(Boolean); // Filter out null values
  })();

  const handleVerificationSubmit = async (data) => {
    const { accessToken, importMethod, repositoryType, message, scanTypeCd, sshUrl, url, verificationType } = data;

    try {
      // Handle repository cloning if needed
      if (importMethod === "repository") {
        try {
          if (accessToken) {
            const repoInfo = {
              url,
              token: accessToken,
            };
            setIsCloneRepositoryPending(true);
            await cloneRepositoryViaPta({ repoInfo, requestData: data });
            setIsCloneRepositoryPending(false);
          } else {
            const repoInfo = {
              url: sshUrl,
            };
            setIsCloneRepositoryPending(true);
            await cloneRepositoryViaSsh({ repoInfo, requestData: data });
            setIsCloneRepositoryPending(false);
          }
        } catch (error) {
          console.error("Repository cloning failed:", error);
          return; // Exit early if repository cloning fails
        }
      }

      // Handle duplicate check and processing
      const onDuplicateFound = (onReuse, onNewScan) => {
        // Support both old single callback and new dual callback patterns
        if (onNewScan) {
          // New pattern: two callbacks
          setPendingReuseCallback(() => onReuse);
          setPendingNewScanCallback(() => onNewScan);
        } else {
          // Legacy pattern: single callback (for backward compatibility)
          setPendingSubmission(() => onReuse);
        }
        setShowConfirmProcessingModal(true);
      };

      if (verificationType && scanToolList && Array.isArray(scanToolList)) {
        const tabIndex = scanToolList.findIndex(tool =>
          tool?.value?.toLowerCase() === verificationType.toLowerCase()
        );
        if (tabIndex >= 0) {
          setActiveVerificationTab(tabIndex);
        }
      }
      // TODO:
      if (verificationType === "sbom") {
        setVerificationType("sbom");
        await handleSbomSubmit(
          {
            projectSeq,
            projectId: projectData.projectId,
            projectName: projectData.projectName,
            requestComment: message,
            ...data,
          },
          onDuplicateFound,
        );
      } else if (verificationType === "clarity") {
        await handleClaritySubmit(
          {
            projectSeq,
            projectId: projectData.projectId,
            projectName: projectData.projectName,
            requestComment: message,
            ...data,
          },
          setProcessingRowId,
          onDuplicateFound,
        );
      }
      else if (verificationType === "clarityair") {
        await handleClarityAirSubmit(
          {
            projectSeq,
            projectId: projectData.projectId,
            projectName: projectData.projectName,
            requestComment: message,
            ...data,
          },
          setProcessingRowId,
          onDuplicateFound,
        );
      } else {
        await handleFossidSubmit(
          {
            projectSeq,
            projectId: projectData.projectId,
            projectName: projectData.projectName,
            requestComment: message,
            ...data,
          },
          setProcessingRowId,
          onDuplicateFound,
        );
      }
    } catch (error) {
      console.error("Error during verification submission:", error);
    }
  };

  const handleConfirmProcessing = () => {
    if (pendingSubmission) {
      pendingSubmission();
    }
    setShowConfirmProcessingModal(false);
    setPendingSubmission(null);
    setPendingReuseCallback(null);
    setPendingNewScanCallback(null);
  };

  const handleReuseScan = () => {
    if (pendingReuseCallback) {
      pendingReuseCallback();
    }
    setShowConfirmProcessingModal(false);
    setPendingReuseCallback(null);
    setPendingNewScanCallback(null);
  };

  const handleNewScan = () => {
    if (pendingNewScanCallback) {
      pendingNewScanCallback();
    }
    setShowConfirmProcessingModal(false);
    setPendingReuseCallback(null);
    setPendingNewScanCallback(null);
  };

  const handleCancelProcessing = () => {
    setShowConfirmProcessingModal(false);
    setPendingSubmission(null);
    setPendingReuseCallback(null);
    setPendingNewScanCallback(null);
  };

  if (getData.isFetching || getLicensePolicyList.isPending) {
    return (
      <div data-testid={`${TEST_ID}-loading`}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid={`${TEST_ID}-page`}>
      {/* Header with Breadcrumbs */}
      <div className="pb-5" data-testid={`${TEST_ID}-header`}>
        <div className="space-y-3">
          <Breadcrumb
            data-testid={`${TEST_ID}-breadcrumb`}
            items={[
              { label: t("navbar.project-list"), href: "/projects" },
              {
                label: t("navbar.project-details"),
              },
            ]}
          />
          {isUploading && (
            <div
              className={`mb-4 flex items-center gap-3 rounded-lg border p-3 animate-pulse ${colors.alertUploadWarning}`}
              data-testid={`${TEST_ID}-upload-warning`}
            >
              <div className={`h-2 w-2 rounded-full ${colors.bgStatusUnconfigured}`} data-testid={`${TEST_ID}-upload-warning-indicator`}></div>
              <span className="text-sm font-medium" data-testid={`${TEST_ID}-upload-warning-message`}>
                {t("project.upload-in-progress-warning") || "An upload is in progress. Please do not leave or refresh the page."}
              </span>
            </div>
          )}
          <div data-testid={`${TEST_ID}-title-wrapper`}>
            <h1
              className={`text-2xl truncate font-bold ${colors.textPrimary}`}
              data-testid={`${TEST_ID}-page-title`}
            >
              {getData.isPending || !displayProjectData ? "Loading..." : displayProjectData.projectName}
            </h1>
          </div>
        </div>
      </div>

      {/* Project Information Section */}
      <div
        className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`}
        data-testid={`${TEST_ID}-information-section`}
      >
        <div
          data-testid={`${TEST_ID}-information-collapse-toggle`}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsProjectInfoCollapsed(!isProjectInfoCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsProjectInfoCollapsed(!isProjectInfoCollapsed);
            }
          }}
          aria-label={isProjectInfoCollapsed ? "Expand Project Information" : "Collapse Project Information"}
        >
          <ChevronUpIcon
            data-testid={`${TEST_ID}-information-collapse-chevron`}
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isProjectInfoCollapsed ? "rotate-180" : ""}`}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={`${TEST_ID}-information-section-title`}>
            {isEditMode ? t("project.project-information-editable-title") : t("project.project-information-title")}
          </h2>
        </div>
        {!isProjectInfoCollapsed && (
          <TabsContainer dataTestId={`${TEST_ID}-info-tabs`} tabs={projectInfoTabs} />
        )}
      </div>
      {/* Preview Components Section */}
      {isPreviewEnabled === true && (
        <div className="mb-4" data-testid={`${TEST_ID}-preview-section`}>
          <PreviewSection
            title={t("project.project-preview.preview-components-list")}
            projectPreviewSeq={projectData}
            projectPreview={projectData?.projectPreview}
            lisencePolicySeq={projectData?.projectPreview?.licensePolicySeq}
            project={displayProjectData}
            onProjectUpdated={getData.refetch}
            creatorList={creatorList}
          />
        </div>
      )}

      {/* Verification Information Section */}
      <div
        className={`mb-4 rounded-2xl p-6 ${colors.bgPrimary} ${colors.borderPrimary}`}
        data-testid={`${TEST_ID}-verification-section`}
      >
        <div className="mb-6 flex items-center justify-between" data-testid={`${TEST_ID}-verification-header`}>
          <h2 className={`mb-2 text-md font-semibold ${colors.textPrimary}`} data-testid={`${TEST_ID}-verification-title`}>
            {t("common.verification-information")}
          </h2>
          <VerificationRequestButton
            data-testid={`${TEST_ID}-add-verification-button`}
            onSubmit={handleVerificationSubmit}
            variant="primary"
            buttonText={t("project.add-verification-request")}
            sbomData={sbomListData}
            scanToolList={scanToolList}
          />
        </div>
        <TabsContainer
          dataTestId={`${TEST_ID}-verification-tabs`}
          tabs={verificationTabs || []}
          defaultActiveTab={activeVerificationTab}
          onTabChange={setActiveVerificationTab}
        />
      </div>

      {/* Member Selection Modal */}
      <MemberSelectionModal
        data-testid={`${TEST_ID}-member-modal`}
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
        onSelect={handleMemberSelect}
        onRemoveMember={canDeleteProjectMemberPermission ? handleRemoveMember : null}
        selectedMember={allMembersInProject}
        creatorList={creatorOptions}
        departmentList={departmentList}
        projectManagerId={effectiveProjectManagerId}
      />

      {/* License Selection Modal */}
      <LicenseSelectionModal
        data-testid={`${TEST_ID}-license-modal`}
        isOpen={modalSelectLicenseOpen}
        onClose={() => setModalSelectLicenseOpen(false)}
        onSelect={handleLicenseSelect}
        selectedLicense={selectedLicense}
      />

      {/* Confirm Processing Modal */}
      <ConfirmProcessingModal
        data-testid={`${TEST_ID}-confirm-processing-modal`}
        isOpen={showConfirmProcessingModal}
        onClose={handleCancelProcessing}
        onConfirm={handleConfirmProcessing}
        onReuseScan={pendingReuseCallback ? handleReuseScan : undefined}
        onNewScan={pendingNewScanCallback ? handleNewScan : undefined}
        title={t("project.duplicate-file-warning")}
        message={pendingReuseCallback && pendingNewScanCallback ? t("project.duplicate-file-warning-message-new") : verificationType === "sbom" ? t("project.sbom-duplicate-file-warning-description") : t("project.duplicate-file-warning-description")}
        confirmText={t("common.yes")}
        cancelText={t("common.cancel-button")}
        reuseText={t("project.reuse-previous-scan-results")}
        newScanText={t("project.perform-new-scan")}
      />

      {/* Confirm Delete Member Modal */}
      <ConfirmDeleteModal
        data-testid={`${TEST_ID}-delete-member-modal`}
        isOpen={showDeleteMemberModal}
        onClose={() => setShowDeleteMemberModal(false)}
        onConfirm={handleConfirmDeleteMember}
        title={t("project.delete-member-confirmation")}
        description={t("project.delete-member-description", {
          count: memberToDelete ? memberToDelete.length : 0,
          plural: memberToDelete && memberToDelete.length > 1 ? "s" : ""
        })}
        loading={deleteLoading}
      />

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid={`${TEST_ID}-delete-document-modal`}
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("project.delete-document-confirmation")}
        description={t("project.delete-document-description", {
          fileName: documentToDelete?.realFileName || 'this document'
        })}
        loading={deleteDocumentLoading}
      />

      {/* Navigation Blocking Modal */}
      <ScanningBlockModal
        data-testid={`${TEST_ID}-navigation-block-modal`}
        isOpen={showNavModal}
        onClose={handleCloseNavModal}
        type="navigation"
        loading={false}
      />

      {/* Refresh Blocking Modal */}
      <ScanningBlockModal
        data-testid={`${TEST_ID}-refresh-block-modal`}
        isOpen={showRefreshModal}
        onClose={handleCloseRefreshModal}
        type="refresh"
        loading={false}
      />

      {/* Full Screen Loading Overlay for Verification Upload */}
      <FullScreenDotLoader
        data-testid={`${TEST_ID}-verification-upload-loader`}
        loading={
          isClarityDuplicateCheckPending ||
          isSbomDuplicateCheckPending ||
          isFossidDuplicateCheckPending ||
          isFossidCreatePending ||
          sbomCreate.isPending ||
          clarityUpload.isPending ||
          fossidUpload.isPending ||
          isCloneRepositoryPending ||
          isClarityAirDuplicateCheckPending ||
          isClarityAirCreatePending ||
          clarityAirUpload.isPending
        }
        text={
          isClarityDuplicateCheckPending ||
            isFossidDuplicateCheckPending ||
            isSbomDuplicateCheckPending ||
            isClarityAirDuplicateCheckPending
            ? t("project.verification-request.validating-file") || "Validating file..."
            : sbomCreate.isPending
              ? t("project.verification-request.uploading-sbom") || "Uploading SBOM verification file..."
              : clarityUpload.isPending
                ? t("project.verification-request.uploading-clarity") || "Uploading Clarity verification file..."
                : fossidUpload.isPending || isFossidCreatePending
                  ? t("project.verification-request.uploading-fossid") || "Uploading FossID verification file..."
                  : isCloneRepositoryPending
                    ? t("project.verification-request.cloning-repository") || "Cloning repository..."
                    : t("project.verification-request.upload-verification-file") || "Uploading verification file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
};

export default ProjectDetail;
